import { Injectable, inject } from '@angular/core';
import type { CombinationPriceGrid, SaveCombinationPricesRequest } from '@models/combination-prices';
import { TranslateService } from '@ngx-translate/core';
import { CrmAccommodationsService } from '@services/crm-accommodations.service';
import { CrmCombinationPricesService } from '@services/crm-combination-prices.service';
import { CrmFishingSpotsService } from '@services/crm-fishing-spots.service';
import { CrmWaterPeriodsService } from '@services/crm-water-periods.service';
import { CrmWaterPriceListsService } from '@services/crm-water-price-lists.service';
import { forkJoin, map, type Observable } from 'rxjs';
import type {
  CombinationPricingInitialData,
  CombinationPricingUnitType,
  CopySource,
} from './combination-pricing.model';

/**
 * Data-access layer for {@link CombinationPricingComponent}: wraps the four CRM services the grid needs,
 * hides the accommodation-vs-fishing-spot endpoint split behind {@link unitType}, and assembles the
 * one-shot initial load. Purely request-building — error toasts, spinners and state live in the caller.
 */
@Injectable({ providedIn: 'root' })
export class CombinationPricingDataService {
  private readonly combinationService = inject(CrmCombinationPricesService);
  private readonly priceListsService = inject(CrmWaterPriceListsService);
  private readonly periodsService = inject(CrmWaterPeriodsService);
  private readonly fishingSpotsService = inject(CrmFishingSpotsService);
  private readonly accommodationsService = inject(CrmAccommodationsService);
  private readonly translate = inject(TranslateService);

  /** Price lists + dimensions + copy sources for a water, with the unit being edited excluded from sources. */
  loadInitial(
    waterId: number,
    unitType: CombinationPricingUnitType,
    unitId: number,
  ): Observable<CombinationPricingInitialData> {
    return forkJoin({
      priceLists: this.priceListsService.getPriceLists(waterId),
      dimensions: this.periodsService.getPricingDimensions(waterId),
      spots: this.fishingSpotsService.getSpots(waterId),
      accommodations: this.accommodationsService.getAccommodations(waterId),
    }).pipe(
      map(({ priceLists, dimensions, spots, accommodations }) => ({
        priceLists,
        seasons: dimensions.seasons,
        stayTypes: dimensions.stayTypes,
        copySources: this.buildCopySources(spots, accommodations, unitType, unitId),
      })),
    );
  }

  /** Load the full grid (all seasons × stay types × cells) for one unit's price list. */
  getGrid(
    unitType: CombinationPricingUnitType,
    waterId: number,
    unitId: number,
    priceListId: number,
  ): Observable<CombinationPriceGrid> {
    return unitType === 'fishing-spot'
      ? this.combinationService.getFishingSpotGrid(waterId, unitId, priceListId)
      : this.combinationService.getAccommodationGrid(waterId, unitId, priceListId);
  }

  /** PUT the whole grid for one unit's price list (the API reconciles to minimal writes). */
  saveGrid(
    unitType: CombinationPricingUnitType,
    waterId: number,
    unitId: number,
    priceListId: number,
    request: SaveCombinationPricesRequest,
  ): Observable<CombinationPriceGrid> {
    return unitType === 'fishing-spot'
      ? this.combinationService.saveFishingSpotGrid(waterId, unitId, priceListId, request)
      : this.combinationService.saveAccommodationGrid(waterId, unitId, priceListId, request);
  }

  /** Other swims + accommodations of this water, minus the unit being edited, as copy sources. */
  private buildCopySources(
    spots: { id: number; number: number | null }[],
    accommodations: { id: number; name: string | null; number: number | null }[],
    unitType: CombinationPricingUnitType,
    unitId: number,
  ): CopySource[] {
    // A swim has no meaningful own name (its title defaults to the water's), so label it by number.
    const spotSources: CopySource[] = spots
      .filter((s) => !(unitType === 'fishing-spot' && s.id === unitId))
      .map((s) => ({
        id: s.id,
        type: 'fishing-spot' as const,
        label: this.translate.instant('waters.fishing-spots.swim-label', {
          number: s.number ?? s.id,
        }),
      }));

    const accommodationSources: CopySource[] = accommodations
      .filter((a) => !(unitType === 'accommodation' && a.id === unitId))
      .map((a) => ({
        id: a.id,
        type: 'accommodation' as const,
        label: this.translate.instant('waters.combination-prices.copy-accommodation-label', {
          name: a.name ?? `#${a.number ?? a.id}`,
        }),
      }));

    return [...accommodationSources, ...spotSources];
  }
}
